import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { Banknote, Plus, RefreshCw, Trash2, Wallet } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ORIGINI = {
  tutte: 'Tutte le origini',
  quota: 'quota in contanti',
  spesa: 'spesa',
  riversamento: 'riversamento sul conto',
  manuale: 'inserito a mano'
};

export const PrimaNota = () => {
  const { token, selectedCondominium } = useAuth();
  const condoId = selectedCondominium?.condominium?.id;
  const userRole = selectedCondominium?.role || 'RESIDENT';
  const isAdmin = userRole === 'ADMIN';
  const annoCorrente = new Date().getFullYear();

  const [anno, setAnno] = useState(String(annoCorrente));
  const [origine, setOrigine] = useState('tutte');
  const [primaNota, setPrimaNota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState({
    data: new Date().toISOString().slice(0, 10),
    tipo: 'ENTRATA',
    descrizione: '',
    importo: ''
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const fetchPrimaNota = useCallback(async () => {
    if (!condoId || !token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/prima-nota?anno=${anno}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setPrimaNota(await res.json());
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore caricamento prima nota');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setLoading(false);
    }
  }, [anno, condoId, token]);

  useEffect(() => {
    fetchPrimaNota();
  }, [fetchPrimaNota]);

  const movimentiFiltrati = useMemo(() => {
    const movimenti = primaNota?.movimenti || [];
    if (origine === 'tutte') return movimenti;
    return movimenti.filter((movimento) => movimento.origine === origine);
  }, [primaNota, origine]);

  const anni = useMemo(() => {
    const base = Number(annoCorrente);
    return [base + 1, base, base - 1, base - 2, base - 3].map(String);
  }, [annoCorrente]);

  const handleSubmit = async () => {
    const importo = Number(String(form.importo).replace(',', '.'));
    if (!form.data || !/^\d{4}-\d{2}-\d{2}$/.test(form.data)) {
      toast.error('Inserisci una data valida');
      return;
    }
    if (!form.descrizione.trim()) {
      toast.error('Inserisci una descrizione');
      return;
    }
    if (!importo || importo <= 0) {
      toast.error("L'importo deve essere maggiore di zero");
      return;
    }

    setSalvando(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/prima-nota/movimento`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          data: form.data,
          tipo: form.tipo,
          descrizione: form.descrizione.trim(),
          importo
        })
      });
      if (res.ok) {
        toast.success('Movimento aggiunto');
        setForm({
          data: new Date().toISOString().slice(0, 10),
          tipo: 'ENTRATA',
          descrizione: '',
          importo: ''
        });
        fetchPrimaNota();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore salvataggio movimento');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSalvando(false);
    }
  };

  const handleDelete = async (movimento) => {
    if (!movimento.id || movimento.origine !== 'manuale') return;
    if (!window.confirm('Eliminare questo movimento manuale dalla prima nota?')) return;

    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/prima-nota/movimento/${movimento.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Movimento eliminato');
        fetchPrimaNota();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore eliminazione movimento');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const totali = primaNota?.totali || {};
  const saldoFinale = totali.saldo_finale || 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Prima nota di cassa"
        subtitle="Contanti incassati, spesi e riversati sul conto"
        actions={(
          <div className="flex items-center gap-2">
            <Select value={anno} onValueChange={setAnno}>
              <SelectTrigger className="w-[120px] bg-white">
                <SelectValue placeholder="Anno" />
              </SelectTrigger>
              <SelectContent>
                {anni.map((item) => (
                  <SelectItem key={item} value={item}>{item}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" className="border-white/40 text-white hover:bg-white/10" onClick={fetchPrimaNota}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Aggiorna
            </Button>
          </div>
        )}
      />

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Entrate</CardDescription>
            <CardTitle className="text-2xl text-emerald-700">{formatCurrency(totali.entrate)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Uscite</CardDescription>
            <CardTitle className="text-2xl text-red-700">{formatCurrency(totali.uscite)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Saldo di cassa a fine anno</CardDescription>
            <CardTitle className={saldoFinale >= 0 ? 'text-2xl text-blue-800' : 'text-2xl text-red-800'}>
              {formatCurrency(saldoFinale)}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {saldoFinale > 0 && (
        <Alert className="border-blue-200 bg-blue-50">
          <Wallet className="h-4 w-4 text-blue-700" />
          <AlertTitle className="text-blue-900">Contante ancora fuori dal conto</AlertTitle>
          <AlertDescription className="text-blue-800">
            A fine {anno} risultano {formatCurrency(saldoFinale)} di contante incassato e non ancora rientrato sul conto del condominio.
          </AlertDescription>
        </Alert>
      )}

      {/* Un saldo di cassa negativo non vuol dire che sia rientrato piu' denaro di
          quanto ne sia entrato: vuol dire che mancano le entrate. Sugli anni le cui
          quote non sono ancora tutte caricate i riversamenti del portiere ci sono
          tutti — l'estratto conto e' completo — mentre gli incassi in contanti si
          conoscono solo in parte, e il saldo scende sotto zero. Detto senza questa
          riga, quel numero si legge come un credito verso il condominio, che e'
          esattamente il contrario di cio' che il dato dice. */}
      {saldoFinale < 0 && (
        <Alert className="border-amber-200 bg-amber-50">
          <Wallet className="h-4 w-4 text-amber-700" />
          <AlertTitle className="text-amber-900">Saldo negativo: mancano delle entrate</AlertTitle>
          <AlertDescription className="text-amber-800">
            A fine {anno} il saldo di cassa è {formatCurrency(saldoFinale)}. Non significa
            che sia rientrato piu' denaro di quanto ne sia stato incassato: significa che
            per questo anno i riversamenti sono tutti noti, perché vengono dall'estratto
            conto, mentre gli incassi in contanti si conoscono solo per le quote già
            caricate e chiuse. Il saldo diventa leggibile quando i pagamenti dell'anno
            sono completi.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <Card>
          <CardHeader className="gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Movimenti</CardTitle>
              <CardDescription>
                Saldo iniziale: {formatCurrency(primaNota?.saldo_iniziale)}
              </CardDescription>
            </div>
            <Select value={origine} onValueChange={setOrigine}>
              <SelectTrigger className="w-full md:w-[220px]">
                <SelectValue placeholder="Origine" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ORIGINI).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Descrizione</TableHead>
                    <TableHead>Origine</TableHead>
                    <TableHead className="text-right">Entrata</TableHead>
                    <TableHead className="text-right">Uscita</TableHead>
                    <TableHead className="text-right">Saldo progressivo</TableHead>
                    {isAdmin && <TableHead className="w-[56px]" />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movimentiFiltrati.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 7 : 6} className="text-center text-muted-foreground py-8">
                        Nessun movimento per i filtri selezionati.
                      </TableCell>
                    </TableRow>
                  ) : movimentiFiltrati.map((movimento, index) => (
                    <TableRow key={`${movimento.origine}-${movimento.id || movimento.riferimento || index}-${movimento.data}`}>
                      <TableCell className="whitespace-nowrap">{movimento.data}</TableCell>
                      <TableCell>
                        <div className="font-medium">{movimento.descrizione}</div>
                        {movimento.riferimento && (
                          <div className="text-xs text-muted-foreground">{movimento.riferimento}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{ORIGINI[movimento.origine] || movimento.origine}</Badge>
                      </TableCell>
                      <TableCell className="text-right text-emerald-700 font-medium">
                        {movimento.tipo === 'ENTRATA' ? formatCurrency(movimento.importo) : ''}
                      </TableCell>
                      <TableCell className="text-right text-red-700 font-medium">
                        {movimento.tipo === 'USCITA' ? formatCurrency(movimento.importo) : ''}
                      </TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(movimento.saldo)}</TableCell>
                      {isAdmin && (
                        <TableCell>
                          {movimento.origine === 'manuale' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Elimina movimento manuale"
                              onClick={() => handleDelete(movimento)}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {isAdmin && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Aggiungi movimento
              </CardTitle>
              <CardDescription>
                Per contanti reali che non emergono da quote, spese o banca.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="prima-nota-data">Data</Label>
                  <Input
                    id="prima-nota-data"
                    type="date"
                    value={form.data}
                    onChange={(e) => setForm({ ...form, data: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select value={form.tipo} onValueChange={(value) => setForm({ ...form, tipo: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ENTRATA">Entrata</SelectItem>
                      <SelectItem value="USCITA">Uscita</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="prima-nota-descrizione">Descrizione</Label>
                <Textarea
                  id="prima-nota-descrizione"
                  rows={3}
                  value={form.descrizione}
                  onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="prima-nota-importo">Importo</Label>
                <Input
                  id="prima-nota-importo"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.importo}
                  onChange={(e) => setForm({ ...form, importo: e.target.value })}
                />
              </div>
              <Button className="w-full" onClick={handleSubmit} disabled={salvando}>
                <Banknote className="h-4 w-4 mr-2" />
                {salvando ? 'Salvataggio...' : 'Registra movimento'}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PrimaNota;
